import { Box, Flex, Modal, ModalBody, ModalCloseButton, ModalContent, ModalFooter, ModalHeader, ModalOverlay, Text } from '@chakra-ui/react'
import { FaRegFileAlt } from 'react-icons/fa'
import { ButtonCmp } from '../../../../../../../components/ButtonCmp'
import { iSolicitacaoReajuste } from '../../../../../../../interfaces'
import { descreverCampoExcluido, iItemSolicitado, iOrigemAjustes, listarItensSolicitados } from '../../../../../utils/ajustes'
import { formatarDataHora } from '../../../../../utils/data'

interface iProps {
  isOpen: boolean
  onClose: () => void
  solicitacao?: iSolicitacaoReajuste
  nomeSolicitante?: string
  anterior?: iOrigemAjustes
}

const rotuloOperacao: Record<iItemSolicitado['operacao'], string> = {
  inclusao: 'Inclusão',
  remocao: 'Exclusão',
  alteracao: 'Alteração'
}

const corOperacao: Record<iItemSolicitado['operacao'], string> = {
  inclusao: 'var(--Azul)',
  remocao: 'var(--Red)',
  alteracao: 'var(--OrangeEscuro)'
}

function ItemDiff ({ item }: { item: iItemSolicitado }): JSX.Element {
  return (
    <Box border={'1px solid var(--Gray5)'} borderRadius={'.25rem'} p={'.75rem'} bgColor={'white'}>
      <Flex gap={'.5rem'} alignItems={'center'} flexWrap={'wrap'} mb={'.5rem'}>
        <Text fontSize={'12px'} fontWeight={'600'} color={corOperacao[item.operacao]} textTransform={'uppercase'}>
          {rotuloOperacao[item.operacao]}
        </Text>
        <Text fontSize={'12px'} color={'var(--Gray3)'}>
          {item.categoria}{item.nivel ? ` · ${item.nivel}` : ''}{item.posicao !== undefined ? ` · ponto ${item.posicao}` : ''}
        </Text>
      </Flex>

      <Flex gap={'.75rem'} flexDir={{ base: 'column', md: 'row' }}>
        {item.valorAnterior !== undefined && (
          <Box flex={'1'}>
            <Text fontSize={'11px'} color={'var(--Gray3)'} mb={'.125rem'}>
              {item.operacao === 'remocao' ? 'Item que pediu para remover' : 'Texto atual'}
            </Text>
            <Text fontSize={'13px'} color={'var(--Gray1)'} bgColor={'var(--RedOpacoBg)'} borderRadius={'.25rem'} p={'.375rem'}>
              {item.valorAnterior}
            </Text>
          </Box>
        )}
        {item.valorSolicitado !== undefined && (
          <Box flex={'1'}>
            <Text fontSize={'11px'} color={'var(--Gray3)'} mb={'.125rem'}>
              {item.operacao === 'inclusao' ? 'Item que pediu para incluir' : 'Texto solicitado'}
            </Text>
            <Text fontSize={'13px'} color={'var(--Gray1)'} bgColor={'var(--AzulOpacoBg)'} borderRadius={'.25rem'} p={'.375rem'}>
              {item.valorSolicitado}
            </Text>
          </Box>
        )}
      </Flex>

      {item.motivo && (
        <Box mt={'.5rem'}>
          <Text fontSize={'11px'} color={'var(--Gray3)'} mb={'.125rem'}>Motivo informado</Text>
          <Text fontSize={'13px'} color={'var(--Gray2)'}>{item.motivo}</Text>
        </Box>
      )}
    </Box>
  )
}

export const ModalDocumentoSolicitacao: React.FC<iProps> = ({ isOpen, onClose, solicitacao, nomeSolicitante, anterior }) => {
  const itens = solicitacao ? listarItensSolicitados(solicitacao, anterior) : []
  const dataPedido = formatarDataHora(solicitacao?.dataSolicitacao)
  // Sem snapshot e registro legado; com snapshot e sem diff o pedido veio so com observacao
  // Comparacao frouxa de proposito: o back serializa campo ausente como null, nao undefined
  const temSnapshot = solicitacao?.estadoAnterior != null && solicitacao?.estadoSolicitado != null
  const semDetalhamento = itens.length === 0
  const exclusoes = solicitacao?.excluidosMotivos ?? []

  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered size={'2xl'} scrollBehavior={'inside'}>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader borderBottom={'1px solid var(--Gray5)'}>
          <Flex alignItems={'center'} gap={'.5rem'} color={'var(--Azul)'}>
            <FaRegFileAlt />
            <Text fontSize={'18px'} fontWeight={'600'}>Solicitações de ajuste</Text>
          </Flex>
          <Text fontSize={'12px'} color={'var(--Gray3)'} fontWeight={'400'} mt={'.25rem'}>
            {nomeSolicitante ? `Solicitado por ${nomeSolicitante}` : 'Solicitação registrada'}
            {dataPedido ? ` · ${dataPedido}` : ''}
          </Text>
        </ModalHeader>
        <ModalCloseButton color={'var(--Gray2)'} bgColor={'transparent'} />

        <ModalBody py={'1rem'}>
          {semDetalhamento
            ? (
              <Flex flexDir={'column'} gap={'.75rem'}>
                <Box bgColor={temSnapshot ? 'var(--AzulOpacoBg)' : 'var(--YellowOpaco)'} borderRadius={'.25rem'} p={'.75rem'}>
                  <Text fontSize={'13px'} color={'var(--Gray1)'}>
                    {temSnapshot
                      ? 'Esta solicitação não alterou itens do descritivo. O solicitante registrou apenas as observações abaixo.'
                      : 'O detalhamento item a item não está disponível para esta solicitação por ser anterior ao registro de histórico. Abaixo estão as informações preservadas.'}
                  </Text>
                </Box>

                {exclusoes.length > 0 && (
                  <Flex flexDir={'column'} gap={'.5rem'}>
                    <Text fontSize={'13px'} fontWeight={'600'} color={'var(--Gray1)'}>Itens com exclusão solicitada</Text>
                    {exclusoes.map((exclusao, i) => {
                      const campo = descreverCampoExcluido(exclusao)

                      return (
                        <Box key={i} border={'1px solid var(--Gray5)'} borderRadius={'.25rem'} p={'.75rem'}>
                          <Flex gap={'.5rem'} alignItems={'center'} flexWrap={'wrap'}>
                            <Text fontSize={'12px'} fontWeight={'600'} color={'var(--Red)'} textTransform={'uppercase'}>Exclusão</Text>
                            <Text fontSize={'13px'} color={'var(--Gray1)'}>
                              {campo.categoria}
                              {campo.nivel ? ` · ${campo.nivel}` : ''}
                              {campo.posicao !== undefined ? ` · item ${campo.posicao}` : ''}
                            </Text>
                          </Flex>
                          {exclusao.motivoExclusao && (
                            <Text fontSize={'12px'} color={'var(--Gray3)'} mt={'.375rem'}>{exclusao.motivoExclusao}</Text>
                          )}
                        </Box>
                      )
                    })}
                  </Flex>
                )}
              </Flex>
              )
            : (
              <Flex flexDir={'column'} gap={'.75rem'}>
                <Text fontSize={'13px'} color={'var(--Gray2)'}>
                  {itens.length === 1 ? '1 item solicitado' : `${itens.length} itens solicitados`}
                </Text>
                {itens.map((item, i) => <ItemDiff key={i} item={item} />)}
              </Flex>
              )}

          {solicitacao?.observacoes && (
            <Box mt={'1rem'} pt={'1rem'} borderTop={'1px solid var(--Gray5)'}>
              <Text fontSize={'13px'} fontWeight={'600'} color={'var(--Gray1)'} mb={'.375rem'}>Observações do solicitante</Text>
              <Text fontSize={'13px'} color={'var(--Gray2)'} whiteSpace={'pre-wrap'}>{solicitacao.observacoes}</Text>
            </Box>
          )}
        </ModalBody>

        <ModalFooter borderTop={'1px solid var(--Gray5)'}>
          <ButtonCmp OutlineColor={'c6'} onClick={onClose}>Fechar</ButtonCmp>
        </ModalFooter>
      </ModalContent>
    </Modal>
  )
}
